import dayjs from "dayjs";
import {
  CalendarIcon,
  CalorieIcon,
  CheckCircleIcon,
  InIcon,
  MealIcon,
  VoiceIcon,
} from "../../assets";
import { IUser } from "../../models";
import {
  capitalizeFirstLetter,
  formatNumberToLocaleString,
} from "../../utils/methods";
import styles from "./styles.module.scss";
import { StatsBlock, UserCall } from "../../components";
import { COLORS } from "../../constants";
import { useEffect, useState } from "react";
import { getTotalProgress } from "../../services/statistics.service";
import { getUserCalls, IUserCall } from "../../services/users.service";

type Props = {
  user: IUser;
  refetchUser: () => void;
};

const UserActionTracker: React.FC<Props> = ({ user, refetchUser }) => {
  const [totalProgress, setTotalProgress] = useState<{
    expectedDeficitSum: number;
    deficitSum: null | number;
  }>({
    expectedDeficitSum: 0,
    deficitSum: 0,
  });

  const fetchTotalProgress = async () => {
    const { data } = await getTotalProgress(user?.id);
    if (data) {
      setTotalProgress(data);
    }
  };

  const fetchUserCalls = async () => {
    const res = await getUserCalls(user?.id);
    if (res) {
      setUserCalls(res);
    }
  };

  const [userCalls, setUserCalls] = useState<IUserCall[]>([]);

  useEffect(() => {
    fetchUserCalls();
    fetchTotalProgress();
  }, [user]);

  return (
    <div className={styles.wrapper}>
      <h6>Action Tracker</h6>
      <div className={styles.coachCallsInfo}>
        <VoiceIcon />
        <div className={styles.infoBlock}>
          <p className={styles.title}>Onboarding Call</p>
          <p className={styles.value}>
            {capitalizeFirstLetter(user?.onboardingCall || "-")}
          </p>
        </div>
        <div className={styles.infoBlock}>
          <p className={styles.title}>Scheduled</p>
          <p className={styles.value}>
            {user?.onboardingCall !== "available" &&
            user?.onboardingCallScheduledAt
              ? dayjs
                  .utc(user?.onboardingCallScheduledAt)
                  ?.format("DD MMM, YYYY")
              : "not scheduled"}
          </p>
        </div>
        <div className={styles.infoBlock}>
          <p className={styles.title}>Available Calls</p>
          <p className={styles.value}>{user?.availableCalls}</p>
        </div>
      </div>
      {userCalls?.length ? (
        <>
          <h6>Pending Calls</h6>
          <div className={styles.userCallsContainer}>
            {userCalls.map((call) => (
              <UserCall
                key={call.id}
                call={call}
                refetchUserCalls={() => {
                  fetchUserCalls();
                  refetchUser();
                }}
              />
            ))}
          </div>
        </>
      ) : null}
      <div className={styles.blocksContainer}>
        <StatsBlock
          title="Meals logged"
          value={`${formatNumberToLocaleString(
            user?.mealsLoggedPercentage,
            1
          )}%`}
          width={180}
          icon={
            <MealIcon
              style={{ fill: COLORS.purple, width: "24px", height: "24px" }}
            />
          }
        />
        <StatsBlock
          title="Completed days"
          value={`${formatNumberToLocaleString(
            user?.completedDaysPercentage || 0,
            1
          )}%`}
          width={180}
          icon={
            <CheckCircleIcon
              style={{ fill: COLORS.green, width: "24px", height: "24px" }}
            />
          }
        />
        <StatsBlock
          title="Total deficit (cal)"
          value={`${formatNumberToLocaleString(
            totalProgress.deficitSum || 0,
            1
          )}/${formatNumberToLocaleString(
            totalProgress.expectedDeficitSum,
            1
          )}`}
          width={180}
          icon={
            <CalorieIcon
              style={{ fill: COLORS.green, width: "24px", height: "24px" }}
            />
          }
        />
        <StatsBlock
          title="Average calories in"
          value={
            <>
              {formatNumberToLocaleString(user?.averageIntake || 0, 1)}{" "}
              <span>cals / day</span>
            </>
          }
          width={180}
          icon={
            <InIcon
              style={{ fill: COLORS.blue, width: "24px", height: "24px" }}
            />
          }
        />

        <StatsBlock
          title="Last logged weight"
          value={
            <>
              {formatNumberToLocaleString(user?.weight?.current || 0, 1)}{" "}
              <span>{user?.weight?.unit}</span>
            </>
          }
          width={180}
          icon={
            <CalendarIcon
              style={{ fill: COLORS.brown, width: "24px", height: "24px" }}
            />
          }
        />
      </div>
    </div>
  );
};

export default UserActionTracker;
